import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { DEVELOPMENT_API_BASE_URL, PRODUCTION_API_BASE_URL } from '../utils/apiConfig';
import { CheckCircle, X } from 'lucide-react';
import { CountryJSON } from '../utils/countryData';
import { useTranslation } from 'react-i18next';

const Register = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        country: 'United Kingdom',
        password: '',
        confirmPassword: '',
        companyName: '',
        phoneNo: '',
        licenceNo: '',
        referralCode: ''
    });

    const [emailOptOut, setEmailOptOut] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState("");
    const [serverError, setServerError] = useState('');
    const { t } = useTranslation();

    // Extract referral code from URL on component mount
    useEffect(() => {
        let refCode = null;
        
        // Check hash-based routing first (for HashRouter: #/Register?ref=CODE)
        const hash = location.hash;
        if (hash && hash.includes('?')) {
            const hashPart = hash.split('?')[1];
            const hashParams = new URLSearchParams(hashPart);
            refCode = hashParams.get('ref');
        }
        
        // Also check regular query params (for direct navigation: ?ref=CODE)
        if (!refCode) {
            const searchParams = new URLSearchParams(location.search);
            refCode = searchParams.get('ref');
        }
        
        if (refCode) {
            setFormData(prev => ({ ...prev, referralCode: refCode.toUpperCase() }));
        }
    }, [location]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error as user edits
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {};
        setServerError('');

        // Validation
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Enter a valid email address';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (!formData.companyName.trim()) {
            newErrors.companyName = 'Company name is required';
        }

        if (!formData.phoneNo.trim()) {
            newErrors.phoneNo = 'Phone number is required';
        }

        if (!emailOptOut) {
            newErrors.emailOptOut = 'Please accept the terms to continue';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);

        try {
            const submitData = new FormData();
            submitData.append('name', formData.name);
            submitData.append('email', formData.email);
            submitData.append('password', formData.password);
            submitData.append('confirmPassword', formData.confirmPassword);
            submitData.append('country', formData.country);
            submitData.append('phoneNo', formData.phoneNo);
            submitData.append('companyName', formData.companyName);
            submitData.append('licenceNo', formData.licenceNo);
            
            // Add referral code if provided
            if (formData.referralCode && formData.referralCode.trim()) {
                submitData.append('ref', formData.referralCode.trim().toUpperCase());
            }

            const response = await fetch(`${DEVELOPMENT_API_BASE_URL}/auth/signup`, {
                method: 'POST',
                body: submitData,
                credentials: 'include'
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSuccess("Merchant account created successfully!");

                // Wait 2 seconds before navigating
                setTimeout(() => {
                    navigate("/");
                }, 2000);
            } else {
                setServerError(data.message || "Registration failed");
            }
        } catch (error) {
            console.error('Registration error:', error);
            setServerError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col lg:flex-row">

            {/* Left Section - Informational Panel */}
            <div className="lg:w-1/2 bg-white lg:bg-gray-50 p-8 lg:p-16 xl:p-20 lg:pl-24 xl:pl-32 2xl:pl-52 flex flex-col justify-between">
                {/* Logo and Brand */}
                <div className="mb-8">
                    <div className="flex items-center space-x-3 mb-8">
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                            <img src="/LOGOM.png" alt="3Pay Logo" id="im" />

                            {/* <KeyRound className="w-5 h-5 text-red-600" /> */}
                        </div>
                        <span className="text-xl font-bold text-black">3PAY</span>
                    </div>
                    {/* <div className="flex items-center mb-8">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
              <span className="text-white font-bold text-xl">3</span>
              <svg className="w-4 h-4 text-white ml-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-2xl font-semibold text-gray-800">3PAY</span>
          </div> */}

                    {/* Features */}
                    <div className="space-y-8">
                        <div className="flex items-start">
                            <div className="w-6 h-6 bg-[#2196F3] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-1.5">{t('Register.getStartedTitle')}</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    {t('Register.getStartedDescription')}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start">
                            <div className="w-6 h-6 bg-[#2196F3] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-1.5">{t('Register.supportBusinessTitle')}</h3>
                                <p className="text-gray-600 leading-relaxed">
                                   {t('Register.supportBusinessDescription')}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start">
                            <div className="w-6 h-6 bg-[#2196F3] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            </div>

                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-1.5"> {t('Register.joinBusinessesTitle')}</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    {t('Register.joinBusinessesDescription')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="text-gray-500 text-sm">
                    © 2025 3PAY • Privacy & Terms
                </div>
            </div>

            {/* Right Section - Form Panel */}
            <div className="lg:w-1/2 bg-white lg:bg-gray-50 p-6 lg:p-8 flex items-center justify-center ">
                <div className="w-full max-w-lg">
                    <div className="bg-white rounded-[30px] shadow-sm border border-gray-200 p-6 lg:p-8">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-6">{t('Register.connectExpert')}</h2>
                        {success && (
                            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
                                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                                <span className="text-green-700 text-sm">{success}</span>
                                <button
                                    onClick={() => setSuccess("")}
                                    className="ml-auto text-green-500 hover:text-green-700"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-3">
                            {/* Name Field */}
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Name
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder={t('Register.enterName')}
                                    className={`w-full px-4 py-2 bg-gray-50 border ${errors.name ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 ${errors.name ? 'focus:ring-red-500' : 'focus:ring-blue-500'} focus:border-transparent text-gray-800 placeholder-gray-400`}
                                />
                                {errors.name && (<p className="mt-1 text-sm text-red-600">{errors.name}</p>)}
                            </div>

                            {/* Company Name Field */}
                            <div>
                                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1.5">
                                   {t('Register.companyName')}
                                </label>
                                <input
                                    type="text"
                                    id="companyName"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleInputChange}
                                    placeholder={t('Register.enterCompanyName')}
                                    className={`w-full px-4 py-2 bg-gray-50 border ${errors.companyName ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 ${errors.companyName ? 'focus:ring-red-500' : 'focus:ring-blue-500'} focus:border-transparent text-gray-800 placeholder-gray-400`}
                                />
                                {errors.companyName && (<p className="mt-1 text-sm text-red-600">{errors.companyName}</p>)}
                            </div>

                            {/* Phone Number Field */}
                            <div>
                                <label htmlFor="phoneNo" className="block text-sm font-medium text-gray-700 mb-1.5">
                                   {t('Register.phoneNumber')}
                                </label>
                                <input
                                    type="text"
                                    id="phoneNo"
                                    name="phoneNo"
                                    value={formData.phoneNo}
                                    onChange={handleInputChange}
                                    placeholder= {t('Register.enterPhoneNumber')}
                                    className={`w-full px-4 py-2 bg-gray-50 border ${errors.phoneNo ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 ${errors.phoneNo ? 'focus:ring-red-500' : 'focus:ring-blue-500'} focus:border-transparent text-gray-800 placeholder-gray-400`}
                                />
                                {errors.phoneNo && (<p className="mt-1 text-sm text-red-600">{errors.phoneNo}</p>)}
                            </div>

                            {/* Licence Number Field */}
                            <div>
                                <label htmlFor="licenceNo" className="block text-sm font-medium text-gray-700 mb-1.5">
                                   {t('Register.licenceNumberOptional')}
                                </label>
                                <input
                                    type="text"
                                    id="licenceNo"
                                    name="licenceNo"
                                    value={formData.licenceNo}
                                    onChange={handleInputChange}
                                    placeholder= {t('Register.enterLicenceNumber')}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-400"
                                />
                            </div>

                            {/* Email Field */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                                 {t('Register.workEmail')}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    placeholder={t('Register.enterWorkEmail')}
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2 bg-gray-50 border ${errors.email ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 ${errors.email ? 'focus:ring-red-500' : 'focus:ring-blue-500'} focus:border-transparent text-gray-800`}
                                />
                                {errors.email && (<p className="mt-1 text-sm text-red-600">{errors.email}</p>)}
                            </div>

                            {/* Country Field */}
                            <div>
                                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1.5">
                                    <span className="flex items-center">
                                        {t('Register.country')}
                                        <svg className="w-4 h-4 text-gray-400 ml-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                    </span>
                                </label>
                                <div className="relative">
                                    <select
                                        id="country"
                                        name="country"
                                        value={formData.country}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 appearance-none pr-10"
                                    >
                                        {CountryJSON.map((country) => (
                                            <option key={country.code} value={country.name}>
                                                {country.name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            {/* Password Field */}
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                                    {t('Register.password')}
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    placeholder= {t('Register.enterPassword')}
                                    className={`w-full px-4 py-2 bg-gray-50 border ${errors.password ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 ${errors.password ? 'focus:ring-red-500' : 'focus:ring-blue-500'} focus:border-transparent text-gray-800 placeholder-gray-400`}
                                />
                                {errors.password && (<p className="mt-1 text-sm text-red-600">{errors.password}</p>)}
                            </div>

                            {/* Confirm Password Field */}
                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                                   {t('Register.confirmPassword')}
                                </label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    placeholder= {t('Register.enterConfirmPassword')}
                                    className={`w-full px-4 py-2 bg-gray-50 border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 ${errors.confirmPassword ? 'focus:ring-red-500' : 'focus:ring-blue-500'} focus:border-transparent text-gray-800 placeholder-gray-400`}
                                />
                                {errors.confirmPassword && (<p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>)}
                            </div>

                            {/* Referral Code Field (Optional) */}
                            <div>
                                <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Referral Code <span className="text-gray-400 font-normal">(Optional)</span>
                                </label>
                                <input
                                    type="text"
                                    id="referralCode"
                                    name="referralCode"
                                    value={formData.referralCode}
                                    onChange={(e) => {
                                        // Convert to uppercase and remove spaces
                                        const value = e.target.value.toUpperCase().replace(/\s/g, '');
                                        setFormData(prev => ({ ...prev, referralCode: value }));
                                        if (errors.referralCode) {
                                            setErrors(prev => ({ ...prev, referralCode: undefined }));
                                        }
                                    }}
                                    placeholder="Enter referral code"
                                    maxLength={10}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-400 uppercase"
                                />
                                {formData.referralCode && (
                                    <p className="mt-1 text-xs text-gray-500">
                                        You'll be linked to the referrer when you sign up
                                    </p>
                                )}
                                {errors.referralCode && (<p className="mt-1 text-sm text-red-600">{errors.referralCode}</p>)}
                            </div>

                            {/* Server Error Display */}
                            {serverError && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <p className="text-sm text-red-600">{serverError}</p>
                                </div>
                            )}

                            {/* Email Opt-out Checkbox */}
                            <div className="flex items-start pt-2 pb-3">
                                <input
                                    type="checkbox"
                                    id="emailOptOut"
                                    checked={emailOptOut}
                                    onChange={(e) => {
                                        setEmailOptOut(e.target.checked);
                                        if (errors.emailOptOut) {
                                            setErrors(prev => ({ ...prev, emailOptOut: undefined }));
                                        }
                                    }}
                                    className={`h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${errors.emailOptOut ? 'border-red-500' : ''}`}
                                />
                                <label htmlFor="emailOptOut" className="ml-3 text-sm text-gray-600">
                                   {t('Register.emailConsent')}.{' '}
                                    <a href="#" className="text-blue-600 font-semibold hover:text-blue-500">Privacy Policy</a>
                                </label>
                            </div>
                            {errors.emailOptOut && (<p className="mt-1 text-sm text-red-600">{errors.emailOptOut}</p>)}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
                                    borderRadius: "15px"
                                }}
                                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none  focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Submitting...' : 'Submit'}
                            </button>

                            {/* Sign in Link */}
                            <div className="text-center">
                                <span className="text-gray-600 text-sm">
                                     {t('Register.haveAccount')}{' '}
                                    <Link to="/" className="text-blue-600 hover:text-blue-500 font-medium">
                                        Sign in
                                    </Link>

                                </span>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Register;
